/**
 * Copyright (c) 2026 Finorda Pty Ltd
 * All rights reserved.
 *
 * This file is part of the Finorda platform.
 * Unauthorised copying, modification or
 * distribution of this software is
 * strictly prohibited.
 *
 * Proprietary and confidential.
 * Governed by Queensland law, Australia.
 */

/**
 * POG API Services
 * Uses canonical fetch-based api client from src/lib/api.js
 */

import { api } from '../lib/api'

const API_URL = import.meta.env.VITE_API_URL || '/api'

// CSV Service
export const csvService = {
  upload: (file, jobNumber, jobName) => {
    const formData = new FormData()
    formData.append('csv', file)
    formData.append('jobNumber', jobNumber)
    formData.append('jobName', jobName)
    return api.upload('/csv/upload', formData)
  },
  process: (csvContent, jobNumber, jobName, deliveryLocation, distributorId, refFields = {}) =>
    api.post('/csv/process', { csvContent, jobNumber, jobName, deliveryLocation, distributorId, ...refFields }),
  getTemplateUrl: () => `${API_URL}/csv/template`,
  getImports: (limit = 20, offset = 0) =>
    api.get(`/csv/imports?limit=${limit}&offset=${offset}`)
}

// Purchase Order Service
export const poService = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => { if (v != null) qs.set(k, v) })
    const q = qs.toString()
    return api.get(`/purchase-orders${q ? '?' + q : ''}`)
  },
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  updateStatus: (id, status, notes) =>
    api.patch(`/purchase-orders/${id}/status`, { status, notes }),
  updateItems: (id, lineItems) =>
    api.put(`/purchase-orders/${id}/items`, { lineItems }),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  getPdfUrl: (id) => {
    const token = localStorage.getItem('token')
    return `${API_URL}/purchase-orders/${id}/pdf?token=${token}`
  }
}

// Supplier Service
export const supplierService = {
  getAll: () => api.get('/suppliers'),
  getMapping: () => api.get('/suppliers/mapping'),
  getMappingDetails: (number) => api.get(`/suppliers/mapping/${number}`),
  updateMapping: (number, supplierId, reason) =>
    api.put(`/suppliers/mapping/${number}`, { supplierId, reason }),
  getMappingHistory: (number) =>
    api.get(`/suppliers/mapping/${number}/history`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  getColumns: (supplierNumber) => api.get(`/suppliers/columns/${supplierNumber}`),
  getAllColumnKeys: () => api.get('/suppliers/columns/all-keys'),
  setColumns: (supplierNumber, columns) =>
    api.put(`/suppliers/columns/${supplierNumber}`, { columns })
}

// Items Service
export const itemService = {
  search: (search, supplierNumber, limit = 50) => {
    const qs = new URLSearchParams()
    if (search) qs.set('search', search)
    if (supplierNumber) qs.set('supplierNumber', supplierNumber)
    qs.set('limit', limit)
    return api.get(`/items?${qs.toString()}`)
  },
  getById: (code) => api.get(`/items/${code}`),
  getSubItems: (code) => api.get(`/items/${code}/subitems`),
  create: (data) => api.post('/items', data),
  update: (code, data) => api.put(`/items/${code}`, data),
  addSubItem: (code, subItem) =>
    api.post(`/items/${code}/subitems/fixed`, subItem),
  removeSubItem: (code, subId) =>
    api.delete(`/items/${code}/subitems/${subId}`)
}

// BOM Expansion Service
export const bomService = {
  expand: (keyItems) => api.post('/bom/expand', { keyItems }),
  expandAndCreate: (data) => api.post('/bom/expand-and-create', data)
}

// Window/Door Service
export const windowDoorService = {
  getOptions: () => api.get('/window-door/options'),
  getSizes: (category, type, glazing) =>
    api.get(`/window-door/sizes?category=${category}&type=${encodeURIComponent(type)}&glazing=${glazing}`),
  getPricing: (glazing) => api.get(`/window-door/pricing?glazing=${glazing}`),
  getPrice: (category, type, size, glazing) =>
    api.get(`/window-door/price?category=${category}&type=${encodeURIComponent(type)}&size=${size}&glazing=${glazing}`)
}

// Auth Service
export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me')
}
